/**
 * Stock Service
 *
 * Stock management and movement operations.
 */

import apiClient, {
  APIResponse,
  PaginatedResponse,
  unwrapResponse,
  unwrapPaginatedResponse,
} from './apiClient';
import { ENDPOINTS, PAGINATION } from '../config/api';

// =============================================================================
// Types
// =============================================================================

export type ProductType = 'MACHINE' | 'PART' | 'CONSUMABLE';
export type MachineStatus = 'AVAILABLE' | 'RENTED' | 'LOA' | 'PCR' | 'MAINTENANCE' | 'SOLD';
export type MovementType = 'ENTREE' | 'SORTIE' | 'TRANSFERT' | 'VENTE' | 'LOCATION' | 'RETOUR' | 'LOA' | 'AJUSTEMENT' | 'CORRECTION';

export interface Product {
  id: string;
  reference: string;
  designation: string;
  description?: string;
  type: ProductType;
  categorie_id: string;
  categorie_nom?: string;
  numero_serie?: string;
  marque?: string;
  modele?: string;
  annee_fabrication?: number;
  status: MachineStatus;
  seuil_alerte: number;
  prix_achat: number;
  prix_vente_public: number;
  prix_vente_grossiste: number;
  prix_vente_revendeur: number;
  tarif_location_journalier: number;
  image_url?: string;
  specs_techniques?: string;
  created_at: string;
  updated_at: string;

  // Computed
  is_serialized: boolean;
  is_machine: boolean;
  is_available: boolean;
}

export interface StockCurrent {
  id: string;
  product_id: string;
  product: Product;
  site_id: string;
  site_nom: string;
  quantite_disponible: number;
  quantite_reservee: number;
  quantite_en_location: number;
  quantite_en_loa: number;
  quantite_en_transit: number;
  valeur_stock_fifo: number;
  derniere_maj: string;

  // Computed
  quantite_totale: number;
  is_below_threshold: boolean;
}

export interface StockByProduct {
  product_id: string;
  product_reference: string;
  product_nom: string;
  stock_total: number;
  stock_by_site: Record<string, number>;
  valeur_stock: number;
  seuil_alerte: number;
  is_below_threshold: boolean;
}

export type MovementStatusType = 'EN_ATTENTE' | 'VALIDE' | 'EN_TRANSIT' | 'EXECUTE' | 'REJETE';

export interface StockMovement {
  id: string;
  code: string;
  product_id: string;
  product_reference: string;
  product_nom: string;
  type_mouvement: MovementType;
  quantite: number;
  site_source_id?: string;
  site_source_nom?: string;
  site_destination_id?: string;
  site_destination_nom?: string;
  cout_unitaire?: number;
  reference_document?: string;
  type_document?: string;
  motif?: string;
  user_id: string;
  user_nom?: string;
  date_mouvement: string;
  created_at: string;
  statut?: MovementStatusType;
  validated_by?: string;
  validated_at?: string;
  motif_rejet?: string;

  // Execution sortie fields
  signature_sortie_magasinier?: string;
  signature_sortie_receptionnaire?: string;
  nom_sortie_receptionnaire?: string;
  executed_sortie_by?: string;
  executed_sortie_at?: string;
  observations_sortie?: string;

  // Execution entree fields
  signature_entree_magasinier?: string;
  signature_entree_remettant?: string;
  nom_entree_remettant?: string;
  executed_entree_by?: string;
  executed_entree_at?: string;
  observations_entree?: string;
}

export interface StockMovementCreate {
  product_id: string;
  type_mouvement: MovementType;
  quantite: number;
  site_source_id?: string;
  site_destination_id?: string;
  cout_unitaire?: number;
  reference_document?: string;
  type_document?: string;
  motif?: string;
}

export interface ProductCreate {
  reference: string;
  designation: string;
  description?: string;
  type: ProductType;
  type_produit_id?: string;
  categorie_id: string;
  numero_serie?: string;
  marque?: string;
  modele?: string;
  annee_fabrication?: number;
  seuil_alerte?: number;
  prix_achat?: number;
  prix_vente_public?: number;
  prix_vente_grossiste?: number;
  prix_vente_revendeur?: number;
  tarif_location_journalier?: number;
  // Initial stock
  stock_initial?: number;
  site_stock_id?: string;
  motif_stock_initial?: string;
}

export interface ProductUpdate {
  reference?: string;
  designation?: string;
  description?: string;
  type?: ProductType;
  type_produit_id?: string;
  categorie_id?: string;
  numero_serie?: string;
  marque?: string;
  modele?: string;
  annee_fabrication?: number;
  seuil_alerte?: number;
  prix_achat?: number;
  prix_vente_public?: number;
  prix_vente_grossiste?: number;
  prix_vente_revendeur?: number;
  tarif_location_journalier?: number;
}

export interface ProductDetail {
  id: string;
  reference: string;
  designation: string;
  description?: string;
  type: ProductType;
  type_produit_id?: string;
  type_produit_libelle?: string;
  base_type?: string;
  categorie_id?: string;
  categorie_nom?: string;
  status: MachineStatus;
  numero_serie?: string;
  marque?: string;
  modele?: string;
  annee_fabrication?: number;
  is_serialized: boolean;
  seuil_alerte: number;
  prix_achat: number;
  prix_vente_public: number;
  prix_vente_grossiste: number;
  prix_vente_revendeur: number;
  tarif_location_journalier: number;
  image_url?: string;
  stock_total?: number;
  stock_by_site?: Record<string, number>;
  created_at: string;
  updated_at: string;
}

export interface Category {
  id: string;
  libelle: string;
  description?: string;
  is_active: boolean;
  ordre: number;
}

// Matches backend ProductListResponse (returned by GET /products)
export interface ProductListItem {
  id: string;
  reference: string;
  designation: string;
  type: ProductType;
  type_produit_id?: string;
  type_produit_libelle?: string;
  base_type?: string;
  status: MachineStatus;
  marque?: string;
  modele?: string;
  numero_serie?: string;
  is_serialized: boolean;
  categorie_nom?: string;
  prix_achat: number;
  prix_vente_public: number;
  prix_vente_grossiste: number;
  prix_vente_revendeur: number;
  tarif_location_journalier: number;
  seuil_alerte: number;
  stock_total?: number;
  stock_disponible?: number;
  stock_reservee?: number;
  stock_en_location?: number;
  stock_en_loa?: number;
  image_url?: string;
}

export interface StockListParams {
  page?: number;
  page_size?: number;
  limit?: number;       // alias for page_size (used by some callers)
  site_id?: string;
  product_type?: ProductType;
  type?: ProductType;   // alias for product_type, sent as 'type' to backend
  below_threshold?: boolean;
  search?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}

export interface MovementListParams {
  page?: number;
  page_size?: number;
  product_id?: string;
  site_id?: string;
  type_mouvement?: MovementType;
  statut?: MovementStatusType;
  date_from?: string;
  date_to?: string;
}

export interface Site {
  id: string;
  code: string;
  nom: string;
  adresse?: string;
  ville?: string;
  telephone?: string;
  is_active: boolean;
  is_default: boolean;
}

export interface StockValuation {
  site_id: string;
  site_nom: string;
  total_value: number;
  total_items: number;
  by_category: Record<string, { value: number; count: number }>;
}

export interface SiteThreshold {
  product_id: string;
  site_id: string;
  seuil: number;
  product_designation?: string;
  site_nom?: string;
  created_at?: string;
  updated_at?: string;
}

export interface RebalancingSuggestion {
  product_id: string;
  product_reference: string;
  product_designation: string;
  site_source_id: string;
  site_source_nom: string;
  site_source_stock: number;
  site_destination_id: string;
  site_destination_nom: string;
  site_destination_stock: number;
  site_destination_seuil: number;
  deficit: number;
  quantite_suggeree: number;
  priorite: 'CRITIQUE' | 'BAS';
}

export interface RebalancingResponse {
  suggestions: RebalancingSuggestion[];
  total_suggestions: number;
  generated_at: string;
}

export interface StockAlertsBySiteResponse {
  alerts_by_site: Record<string, StockAlertItem[]>;
  total_alerts: number;
  sites_concerned: number;
  generated_at: string;
}

export interface ExecuteSortieData {
  signature_magasinier?: string;
  signature_receptionnaire?: string;
  nom_receptionnaire: string;
  observations?: string;
}

export interface ExecuteEntreeData {
  signature_magasinier?: string;
  signature_remettant?: string;
  nom_remettant: string;
  observations?: string;
}

export interface StockAlertItem {
  product_id: string;
  site_id: string;
  product_reference: string;
  product_designation: string;
  site_nom: string;
  site_code: string;
  quantite_disponible: number;
  seuil_alerte: number;
  is_below_threshold: boolean;
  is_critique: boolean;
}

// =============================================================================
// Stock Service
// =============================================================================

export const stockService = {
  /**
   * Create a new product
   */
  async createProduct(data: ProductCreate): Promise<Product> {
    const response = await apiClient.post<APIResponse<Product>>(
      ENDPOINTS.PRODUCTS,
      data
    );
    return unwrapResponse(response);
  },

  async getProduct(id: string): Promise<ProductDetail> {
    const response = await apiClient.get<APIResponse<ProductDetail>>(
      `${ENDPOINTS.PRODUCTS}/${id}`
    );
    return unwrapResponse(response);
  },

  async updateProduct(id: string, data: ProductUpdate): Promise<ProductDetail> {
    const response = await apiClient.patch<APIResponse<ProductDetail>>(
      `${ENDPOINTS.PRODUCTS}/${id}`,
      data
    );
    return unwrapResponse(response);
  },

  /**
   * List active categories
   */
  async listCategories(): Promise<Category[]> {
    const response = await apiClient.get<PaginatedResponse<Category>>(
      ENDPOINTS.CATEGORIES,
      { params: { page_size: 100, active_only: true } }
    );
    return unwrapPaginatedResponse(response).data;
  },

  /**
   * List products with stock info (calls GET /products)
   */
  async listStock(params: StockListParams = {}) {
    const { page = PAGINATION.DEFAULT_PAGE, page_size, limit, product_type, type, search, site_id, sort_by, sort_dir } = params;
    const effectivePageSize = page_size || limit || PAGINATION.DEFAULT_PAGE_SIZE;
    const effectiveType = type || product_type;

    const response = await apiClient.get<PaginatedResponse<ProductListItem>>(
      ENDPOINTS.PRODUCTS,
      {
        params: {
          page,
          page_size: effectivePageSize,
          ...(effectiveType ? { type: effectiveType } : {}),
          ...(search ? { search } : {}),
          ...(site_id ? { site_id } : {}),
          ...(sort_by ? { sort_by } : {}),
          ...(sort_dir ? { sort_dir } : {}),
        },
      }
    );

    return unwrapPaginatedResponse(response);
  },

  /**
   * Get stock for a specific product across all sites
   */
  async getProductStock(productId: string): Promise<StockByProduct> {
    const response = await apiClient.get<APIResponse<StockByProduct>>(
      `${ENDPOINTS.STOCK}/products/${productId}`
    );
    return unwrapResponse(response);
  },

  /**
   * Get stock for a specific site
   */
  async getSiteStock(siteId: string): Promise<StockCurrent[]> {
    const response = await apiClient.get<APIResponse<{ items: StockCurrent[]; totaux: any }>>(
      `${ENDPOINTS.STOCK}/etat`,
      { params: { site_id: siteId } }
    );
    const data = unwrapResponse(response);
    return data.items || [];
  },

  /**
   * Get critical stock items (below threshold)
   */
  async getCriticalStock(): Promise<StockCurrent[]> {
    const response = await apiClient.get<APIResponse<{ items: StockCurrent[]; totaux: any }>>(
      `${ENDPOINTS.STOCK}/etat`,
      { params: { below_threshold_only: true } }
    );
    const data = unwrapResponse(response);
    return data.items || [];
  },

  /**
   * List stock movements with filters
   */
  async listMovements(params: MovementListParams = {}) {
    const {
      page = PAGINATION.DEFAULT_PAGE,
      page_size = PAGINATION.DEFAULT_PAGE_SIZE,
      ...filters
    } = params;

    const response = await apiClient.get<PaginatedResponse<StockMovement>>(
      `${ENDPOINTS.STOCK}/mouvements`,
      {
        params: { page, page_size, ...filters },
      }
    );

    return unwrapPaginatedResponse(response);
  },

  /**
   * Create a stock movement
   */
  async createMovement(data: StockMovementCreate): Promise<StockMovement> {
    const response = await apiClient.post<APIResponse<StockMovement>>(
      `${ENDPOINTS.STOCK}/mouvements?confirm=true`,
      data
    );
    return unwrapResponse(response);
  },

  /**
   * Get a single movement by ID
   */
  async getMovement(id: string): Promise<StockMovement> {
    // No individual movement endpoint - fetch from list
    const result = await this.listMovements({ page: 1, page_size: 1 });
    const movement = result.data.find((m: StockMovement) => m.id === id);
    if (!movement) throw new Error('Movement not found');
    return movement;
  },

  /**
   * Validate a pending movement (GERANT/ADMIN only)
   */
  async validateMovement(movementId: string): Promise<StockMovement> {
    const response = await apiClient.post<APIResponse<StockMovement>>(
      `${ENDPOINTS.STOCK}/mouvements/${movementId}/valider`
    );
    return unwrapResponse(response);
  },

  /**
   * Reject a pending movement (GERANT/ADMIN only)
   */
  async rejectMovement(movementId: string, motif: string): Promise<StockMovement> {
    const response = await apiClient.post<APIResponse<StockMovement>>(
      `${ENDPOINTS.STOCK}/mouvements/${movementId}/rejeter`,
      { motif }
    );
    return unwrapResponse(response);
  },

  /**
   * Get pending movements
   */
  async getPendingMovements(page?: number, pageSize?: number) {
    const response = await apiClient.get<PaginatedResponse<StockMovement>>(
      `${ENDPOINTS.STOCK}/mouvements/en-attente`,
      {
        params: {
          page: page || PAGINATION.DEFAULT_PAGE,
          page_size: pageSize || PAGINATION.DEFAULT_PAGE_SIZE,
        },
      }
    );
    return unwrapPaginatedResponse(response);
  },

  /**
   * Get stock valuation by site
   */
  async getValuation(): Promise<StockValuation[]> {
    // Use dashboard/stock endpoint
    try {
      const response = await apiClient.get<APIResponse<{
        stocks: StockValuation[];
      }>>(`${ENDPOINTS.DASHBOARD}/stock`);
      return unwrapResponse(response).stocks || [];
    } catch {
      return [];
    }
  },

  /**
   * List all sites
   */
  async listSites(): Promise<Site[]> {
    const response = await apiClient.get<APIResponse<Site[]>>(ENDPOINTS.SITES);
    return unwrapResponse(response);
  },

  /**
   * Quick stock entry (receipt)
   */
  async stockEntry(
    productId: string,
    siteId: string,
    quantity: number,
    options?: {
      pricePerUnit?: number;
      referenceDoc?: string;
    }
  ): Promise<StockMovement> {
    return this.createMovement({
      product_id: productId,
      type_mouvement: 'ENTREE',
      quantite: quantity,
      site_source_id: siteId,
      cout_unitaire: options?.pricePerUnit,
      reference_document: options?.referenceDoc,
    });
  },

  /**
   * Quick stock exit (dispatch)
   */
  async stockExit(
    productId: string,
    siteId: string,
    quantity: number,
    options?: {
      referenceDoc?: string;
      typeDoc?: string;
      motif?: string;
    }
  ): Promise<StockMovement> {
    return this.createMovement({
      product_id: productId,
      type_mouvement: 'SORTIE',
      quantite: quantity,
      site_source_id: siteId,
      reference_document: options?.referenceDoc,
      type_document: options?.typeDoc,
      motif: options?.motif,
    });
  },

  /**
   * Stock transfer between sites
   */
  async stockTransfer(
    productId: string,
    fromSiteId: string,
    toSiteId: string,
    quantity: number,
    _notes?: string
  ): Promise<StockMovement> {
    return this.createMovement({
      product_id: productId,
      type_mouvement: 'TRANSFERT',
      quantite: quantity,
      site_source_id: fromSiteId,
      site_destination_id: toSiteId,
    });
  },

  /**
   * Stock adjustment (inventory correction)
   */
  async stockAdjustment(
    productId: string,
    siteId: string,
    quantity: number,
    motif: string
  ): Promise<StockMovement> {
    // Positive quantity = increase, negative = decrease
    const isPositive = quantity > 0;

    return this.createMovement({
      product_id: productId,
      type_mouvement: 'AJUSTEMENT',
      quantite: Math.abs(quantity),
      site_source_id: siteId,
      motif,
    });
  },

  // ---------------------------------------------------------------------------
  // Workflow Execution (signatures)
  // ---------------------------------------------------------------------------

  /**
   * Execute physical exit (sortie) with signatures
   * - Simple movements: VALIDE → EXECUTE
   * - Transfers: VALIDE → EN_TRANSIT
   */
  async executeSortie(movementId: string, data: ExecuteSortieData): Promise<StockMovement> {
    const response = await apiClient.post<APIResponse<StockMovement>>(
      `${ENDPOINTS.STOCK}/mouvements/${movementId}/executer-sortie`,
      data
    );
    return unwrapResponse(response);
  },

  /**
   * Execute physical entry (réception) with signatures
   * - Transfers: EN_TRANSIT → EXECUTE
   * - Entries/Returns: VALIDE → EXECUTE
   */
  async executeEntree(movementId: string, data: ExecuteEntreeData): Promise<StockMovement> {
    const response = await apiClient.post<APIResponse<StockMovement>>(
      `${ENDPOINTS.STOCK}/mouvements/${movementId}/executer-entree`,
      data
    );
    return unwrapResponse(response);
  },

  // ---------------------------------------------------------------------------
  // Transfers
  // ---------------------------------------------------------------------------

  async listTransfers(params: { page?: number; page_size?: number; site_id?: string; date_from?: string; date_to?: string } = {}) {
    const { page = 1, page_size = 20, ...filters } = params;
    const response = await apiClient.get<PaginatedResponse<StockMovement>>(`${ENDPOINTS.STOCK}/mouvements/transferts`, {
      params: { page, page_size, ...filters },
    });
    return unwrapPaginatedResponse(response);
  },

  // ---------------------------------------------------------------------------
  // Per-Site Alert Thresholds
  // ---------------------------------------------------------------------------

  async listSiteThresholds(params: { product_id?: string; site_id?: string } = {}): Promise<SiteThreshold[]> {
    const response = await apiClient.get<APIResponse<SiteThreshold[]>>(`${ENDPOINTS.STOCK}/seuils-site`, { params });
    return unwrapResponse(response);
  },

  async upsertSiteThresholds(seuils: { product_id: string; site_id: string; seuil: number }[]): Promise<{ updated: number }> {
    const response = await apiClient.put<APIResponse<{ updated: number }>>(`${ENDPOINTS.STOCK}/seuils-site`, { seuils });
    return unwrapResponse(response);
  },

  async deleteSiteThreshold(product_id: string, site_id: string): Promise<void> {
    await apiClient.delete(`${ENDPOINTS.STOCK}/seuils-site`, { params: { product_id, site_id } });
  },

  // ---------------------------------------------------------------------------
  // Rebalancing Suggestions
  // ---------------------------------------------------------------------------

  async getRebalancingSuggestions(): Promise<RebalancingResponse> {
    const response = await apiClient.get<APIResponse<RebalancingResponse>>(`${ENDPOINTS.STOCK}/rebalancing`);
    return unwrapResponse(response);
  },

  // ---------------------------------------------------------------------------
  // Per-Site Stock Alerts
  // ---------------------------------------------------------------------------

  async getStockAlertsBySite(): Promise<StockAlertsBySiteResponse> {
    const response = await apiClient.get<APIResponse<StockAlertsBySiteResponse>>(`${ENDPOINTS.STOCK}/alertes/par-site`);
    return unwrapResponse(response);
  },

  // ---------------------------------------------------------------------------
  // Export (état du stock)
  // ---------------------------------------------------------------------------

  async exportStock(
    format: 'pdf' | 'xlsx',
    params: { site_id?: string; produit_id?: string; below_threshold_only?: boolean; critique_only?: boolean } = {}
  ): Promise<Blob> {
    const response = await apiClient.get(`${ENDPOINTS.STOCK}/export`, {
      params: { format, ...params },
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Download the stock state export and trigger a file save in the browser.
   */
  async downloadStock(
    format: 'pdf' | 'xlsx',
    params: { site_id?: string; produit_id?: string; below_threshold_only?: boolean; critique_only?: boolean } = {}
  ): Promise<void> {
    const blob = await this.exportStock(format, params);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `etat_stock_${new Date().toISOString().split('T')[0]}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },
};

export default stockService;
